import React, { createContext, useContext, useEffect, useRef, useState, ReactNode, useCallback } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { authService } from '@/services/authService';
import { AuthContextType, UserProfile, UserRole, UserPermission } from '@/types/auth';
import { useCurrentRentalPoint } from '@/stores/useCurrentRentalPoint';
import { ensureDefaultTags } from '@/services/settings/defaultTagsService';
import { ensureWorkerAuthReady } from '@/utils/authTokenSync';
import { useLogger } from '@/utils/logging/logger';
import { supabaseWorker } from '@/integrations/supabase/workerClient';

/**
 * @fileoverview AuthContext - базовая аутентификация согласно STYLEGUIDE.md
 * Предоставляет только user, session, profile. UserMetadata загружается отдельно через useUserMetadata
 * Устранена циклическая зависимость между AuthContext и useUserMetadata
 */

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Module-scoped guards to ensure a single auth listener across remounts
let authListenerMountCount = 0;
let authListenerUnsubscribe: (() => void) | null = null;
let authListenerInitialized = false;

/**
 * Проверяет и инициализирует доменные статусы инвентаря при первом входе
 * Это критично для корректной работы на всех ОС (Windows, macOS)
 * 
 * ИСПРАВЛЕНИЕ: Добавлена retry логика и детальное логирование для Windows
 */
async function ensureDomainStatuses(userId: string, logger: ReturnType<typeof useLogger>): Promise<void> {
  const MAX_RETRIES = 3;
  const RETRY_DELAY = 500; // ms
  
  // Helper для retry логики
  async function retryQuery<T>(
    queryFn: () => Promise<{ data: T | null; error: any }>,
    operation: string,
    retries = MAX_RETRIES
  ): Promise<{ data: T | null; error: any }> {
    for (let attempt = 1; attempt <= retries; attempt++) {
      logger.info(`AuthProvider: ${operation} - attempt ${attempt}/${retries}`);
      const result = await queryFn();
      
      if (!result.error) {
        logger.info(`AuthProvider: ${operation} - success on attempt ${attempt}`);
        return result;
      }
      
      logger.warn(`AuthProvider: ${operation} - error on attempt ${attempt}:`, result.error);
      
      if (attempt < retries) {
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * attempt));
      }
    }
    
    return await queryFn(); // Final attempt
  }
  
  try {
    logger.info(`AuthProvider: Starting domain statuses initialization for user ${userId}`);
    
    // ========== INVENTORY STATUSES ==========
    logger.info('AuthProvider: Checking inventory_statuses...');
    
    const statusesResult = await retryQuery(
      async () => {
        const result = await supabaseWorker
          .from('inventory_statuses')
          .select('id, name')
          .eq('user_id', userId)
          .limit(5);
        return { data: result.data, error: result.error };
      },
      'Checking inventory_statuses'
    );

    if (statusesResult.error) {
      logger.error('AuthProvider: CRITICAL - Failed to check inventory_statuses after retries:', statusesResult.error);
      // Не возвращаемся, пытаемся создать статусы принудительно
    }

    const statusesData = statusesResult.data;
    const statusesCount = Array.isArray(statusesData) ? statusesData.length : 0;
    logger.info(`AuthProvider: Found ${statusesCount} existing inventory statuses`);

    if (statusesCount === 0) {
      logger.info('AuthProvider: No inventory statuses found, creating defaults...');
      
      const defaultStatuses = [
        { name: 'Доступен', color: '#10b981', description: 'Статус инвентаря: Доступен', user_id: userId, sort_order: 1 },
        { name: 'В аренде', color: '#3b82f6', description: 'Статус инвентаря: В аренде', user_id: userId, sort_order: 2 },
        { name: 'В ремонте', color: '#f59e0b', description: 'Статус инвентаря: В ремонте', user_id: userId, sort_order: 3 },
      ];

      const insertResult = await retryQuery(
        async () => {
          const result = await supabaseWorker
            .from('inventory_statuses')
            .insert(defaultStatuses)
            .select('id, name');
          return { data: result.data, error: result.error };
        },
        'Creating inventory_statuses'
      );

      if (insertResult.error) {
        logger.error('AuthProvider: CRITICAL - Failed to create inventory statuses:', insertResult.error);
      } else {
        const created = Array.isArray(insertResult.data) ? insertResult.data.length : 0;
        logger.info(`AuthProvider: ✅ Successfully created ${created} inventory statuses`);
      }
    } else {
      logger.info('AuthProvider: ✅ Inventory statuses already exist, skipping creation');
    }

    // ========== INVENTORY CONDITIONS ==========
    logger.info('AuthProvider: Checking inventory_conditions...');
    
    const conditionsResult = await retryQuery(
      async () => {
        const result = await supabaseWorker
          .from('inventory_conditions')
          .select('id, name')
          .eq('user_id', userId)
          .limit(5);
        return { data: result.data, error: result.error };
      },
      'Checking inventory_conditions'
    );

    if (conditionsResult.error) {
      logger.error('AuthProvider: CRITICAL - Failed to check inventory_conditions after retries:', conditionsResult.error);
      // Не возвращаемся, пытаемся создать условия принудительно
    }

    const conditionsData = conditionsResult.data;
    const conditionsCount = Array.isArray(conditionsData) ? conditionsData.length : 0;
    logger.info(`AuthProvider: Found ${conditionsCount} existing inventory conditions`);

    if (conditionsCount === 0) {
      logger.info('AuthProvider: No inventory conditions found, creating defaults...');
      
      const defaultConditions = [
        { name: 'Хорошее', color: '#10b981', description: 'Состояние инвентаря: Хорошее', user_id: userId, sort_order: 1 },
        { name: 'Норм', color: '#f59e0b', description: 'Состояние инвентаря: Норм', user_id: userId, sort_order: 2 },
        { name: 'Плохое', color: '#ef4444', description: 'Состояние инвентаря: Плохое', user_id: userId, sort_order: 3 },
      ];

      const insertResult = await retryQuery(
        async () => {
          const result = await supabaseWorker
            .from('inventory_conditions')
            .insert(defaultConditions)
            .select('id, name');
          return { data: result.data, error: result.error };
        },
        'Creating inventory_conditions'
      );

      if (insertResult.error) {
        logger.error('AuthProvider: CRITICAL - Failed to create inventory conditions:', insertResult.error);
      } else {
        const created = Array.isArray(insertResult.data) ? insertResult.data.length : 0;
        logger.info(`AuthProvider: ✅ Successfully created ${created} inventory conditions`);
      }
    } else {
      logger.info('AuthProvider: ✅ Inventory conditions already exist, skipping creation');
    }
    
    logger.info('AuthProvider: Domain statuses initialization completed');
  } catch (error) {
    logger.error('AuthProvider: CRITICAL ERROR in ensureDomainStatuses:', error);
  }
}

export const useAuth = (): AuthContextType => {
  const logger = useLogger('AuthContext');
  const context = useContext(AuthContext);
  if (context === undefined) {
    // Добавляем более детальную информацию об ошибке для отладки
    logger.error('useAuth called outside of AuthProvider context', {
      stackTrace: new Error().stack,
      timestamp: new Date().toISOString(),
      location: typeof window !== 'undefined' ? window.location.href : 'unknown'
    });
    throw new Error('useAuth must be used within an AuthProvider. Make sure the component is wrapped in <AuthProvider>.');
  }
  return context;
};

// Re-export types for compatibility
export type { UserRole };

type AuthProviderProps = {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const logger = useLogger('AuthContext');
  const loggerRef = useRef(logger);
  // keep logger in ref to avoid effect dependency churn
  useEffect(() => {
    loggerRef.current = logger;
  }, [logger]);
  // Log initialization only once per mount to avoid noise on re-renders
  useEffect(() => {
    loggerRef.current.info('AuthProvider: Initializing AuthProvider...');
  }, []);
  
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [originalRole, setOriginalRole] = useState<UserRole | null>(null);
  const profileLoadRef = useRef<{ inFlight: boolean; userId?: string }>({ inFlight: false });
  
  // Получаем функцию для сброса пункта проката при смене пользователя
  const { setCurrentUser } = useCurrentRentalPoint();
  
  // ИСПРАВЛЕНО: AuthContext предоставляет только базовые данные аутентификации
  // userMetadata загружается отдельно через useUserMetadata хук в компонентах
  
  const isAuthenticated = !!user && !!session;

  // ИСПРАВЛЕНО: Упрощенная функция для проверки данных пользователя согласно STYLEGUIDE.md
  // AuthContext предоставляет только базовые данные аутентификации, userMetadata загружается отдельно
  const checkUserData = useCallback(async (userId: string) => {
    try {
      logger.info('[AuthContext] Checking basic user data for:', { userId });
      
      // КРИТИЧНО: Завершаем setup пользователя (account, rental_point, staff)
      // Это быстро, т.к. выполняется асинхронно после регистрации
      try {
        const { data: setupResult, error: setupError } = await supabase.rpc('complete_user_setup', {
          user_uuid: userId
        });
        
        if (setupError) {
          logger.warn('[AuthContext] User setup completion failed (non-blocking):', setupError);
        } else {
          logger.info('[AuthContext] User setup completed:', setupResult);
        }
      } catch (setupErr) {
        logger.warn('[AuthContext] User setup error (non-blocking):', setupErr);
      }
      
      // Проверяем наличие заказов (включая аренды)
      const { data: orders, error: ordersError } = await supabase
        .from('orders')
        .select('id')
        .eq('user_id', userId)
        .limit(1);
      
      if (ordersError) {
        logger.error('[AuthContext] Orders check error:', ordersError);
      }
      
      // Проверяем наличие клиентов
      const { data: clients, error: clientsError } = await supabase
        .from('clients')
        .select('id')
        .eq('user_id', userId)
        .limit(1);
      
      if (clientsError) {
        logger.error('[AuthContext] Clients check error:', clientsError);
      }
      
      // Проверяем наличие инвентаря
      const { data: inventory, error: inventoryError } = await supabase
        .from('inventory')
        .select('id')
        .eq('user_id', userId)
        .limit(1);
      
      if (inventoryError) {
        logger.error('[AuthContext] Inventory check error:', inventoryError);
      }
      
      const hasOrders = (orders && orders.length > 0) || false;
      const hasClients = (clients && clients.length > 0) || false;
      const hasInventory = (inventory && inventory.length > 0) || false;
      const isNewUser = !hasOrders && !hasClients && !hasInventory;
      
      logger.info('[AuthContext] Basic user data checked:', {
        hasOrders,
        hasClients,
        hasInventory,
        isNewUser
      });
      
      // Устанавливаем текущего пользователя в store пунктов проката
      setCurrentUser(userId);
      
      // Инициализация тегов перенесена после синхронизации токенов
      
      logger.info('[AuthContext] Basic user data check completed', { userId });
      
      return {
        hasOrders,
        hasClients,
        hasInventory,
        isNewUser
      };
    } catch (error) {
      logger.error('[AuthContext] Error checking user data:', error);
      return null;
    }
  }, [logger, setCurrentUser]);

  // Обновленная функция signIn
  const signIn = async (email: string, password: string) => {
    try {
      await authService.signIn(email, password);
      return { error: null };
    } catch (error: unknown) {
      logger.error('AuthContext: Sign in error:', error);
      return { error };
    }
  };

  // Обновленная функция signUp
  const signUp = async (email: string, password: string, userData?: Record<string, unknown>) => {
    try {
      logger.info('AuthContext: Starting sign up process...');
      
      const result = await authService.signUp(email, password, userData);
      logger.info('AuthContext: Sign up result:', result);
      
      // ИСПРАВЛЕНО: Возвращаем только { error } согласно AuthContextType
      if ('error' in result) {
        return { error: result.error };
      } else {
        // Успешная регистрация - возвращаем null error
        return { error: null };
      }
    } catch (error: unknown) {
      logger.error('AuthContext: Sign up error:', error);
      return { error };
    }
  };

  // Функция signOut
  const signOut = async (): Promise<void> => {
    try {
      await authService.signOut();
      setCurrentUser(null);
    } catch (error: unknown) {
      logger.error('AuthContext: Sign out error:', error);
      throw error;
    }
  };

  // Функция hasPermission - поддерживает как роли, так и разрешения
  const hasPermission = (rolesOrPermissions: UserRole | UserRole[] | UserPermission | UserPermission[] | string | string[]): boolean => {
    if (!profile) {
      loggerRef.current.warn('hasPermission: No profile available');
      return false;
    }

    const items = Array.isArray(rolesOrPermissions) ? rolesOrPermissions : [rolesOrPermissions];
    
    // Проверяем роли
    if (items.some(item => typeof item === 'string' && ['admin', 'manager', 'operator'].includes(item))) {
      const hasRole = items.includes(profile.role as string);
      loggerRef.current.debug('hasPermission: Role check', {
        requiredRoles: items,
        userRole: profile.role,
        hasAccess: hasRole
      });
      return hasRole;
    }
    
    // Здесь можно добавить логику для проверки разрешений
    loggerRef.current.debug('hasPermission: No role check needed, allowing access');
    return true; // Временно разрешаем все
  };

  const refreshProfile = async () => {
    if (!user?.id) return;
    
    try {
      const profileData = await authService.loadUserProfile(user.id, session);
      if (profileData) {
        const enhancedProfile: UserProfile = {
          ...profileData,
          name: profileData.fullName || user.email || 'Unknown User',
          email: user.email || '',
          avatar: undefined,
          department: undefined,
          lastLogin: undefined,
          rentalPoints: [],
        };
        setProfile(enhancedProfile);
      }
    } catch (error) {
      logger.error('AuthContext: Error refreshing profile:', error);
    }
  };

  const switchRole = (_role: UserRole) => {
    // Implement role switching logic here
  };

  // Additional auth methods for compatibility
  const signInWithPhone = async (_phone: string): Promise<void> => {
    throw new Error('Phone sign-in not implemented');
  };

  const signInWithTelegram = async (_data: unknown): Promise<void> => {
    throw new Error('Telegram sign-in not implemented');
  };

  const resetPassword = async (_email: string): Promise<void> => {
    throw new Error('Password reset not implemented');
  };

  const updatePassword = async (_password: string): Promise<void> => {
    throw new Error('Password update not implemented');
  };

  const verifyOtp = async (_phone: string, _otp: string): Promise<void> => {
    throw new Error('OTP verification not implemented');
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    authListenerMountCount += 1;

    if (authListenerInitialized) {
      loggerRef.current.info('AuthProvider: Auth state listener already initialized, skipping setup');
      return () => {
        authListenerMountCount -= 1;
        if (authListenerMountCount === 0 && authListenerUnsubscribe) {
          try { authListenerUnsubscribe(); } catch { /* noop */ }
          authListenerUnsubscribe = null;
          authListenerInitialized = false;
          loggerRef.current.info('AuthProvider: Auth state listener fully cleaned up');
        }
      };
    }

    loggerRef.current.info('AuthProvider: Setting up auth state listener...');

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        loggerRef.current.info('AuthProvider: Auth state changed:', { event, hasSession: !!session, hasUser: !!session?.user });
        
        setSession(session);
        setUser(session?.user ?? null);
        
        loggerRef.current.debug('AuthProvider: Session details:', {
          hasSession: !!session,
          hasUser: !!session?.user,
          userId: session?.user?.id,
          userEmail: session?.user?.email,
          sessionType: typeof session,
          userType: typeof session?.user
        });
        
        if (session?.user) {
          // Prevent duplicate concurrent profile loads for the same user
          if (profileLoadRef.current.inFlight && profileLoadRef.current.userId === session.user.id) {
            loggerRef.current.debug('AuthProvider: Skipping duplicate profile load for same user');
            setIsLoading(false);
            return;
          }
          profileLoadRef.current.inFlight = true;
          profileLoadRef.current.userId = session.user.id;
          loggerRef.current.info('AuthProvider: Starting profile loading for user', { userId: session.user.id });
          
          try {
            loggerRef.current.debug('AuthProvider: Calling loadUserProfile with session...');
            let profileData = await authService.loadUserProfile(session.user.id, session);
            loggerRef.current.debug('AuthProvider: Profile data received:', profileData);
            
            // КРИТИЧНО: Если профиль не найден (новый пользователь), вызываем setup
            if (!profileData) {
              loggerRef.current.warn('AuthProvider: No profile data, checking if user needs setup...');
              
              // Проверяем, есть ли у пользователя запись в staff
              const { data: staffCheck } = await supabase
                .from('staff')
                .select('id')
                .eq('user_id', session.user.id)
                .maybeSingle();
              
              // Только если нет записи в staff, вызываем setup
              if (!staffCheck) {
                loggerRef.current.info('AuthProvider: User has no staff record, running complete_user_setup...');
                try {
                  const { data: setupResult, error: setupError } = await supabase.rpc('complete_user_setup', {
                    user_uuid: session.user.id
                  });
                  
                  if (setupError) {
                    loggerRef.current.error('AuthProvider: User setup failed:', setupError);
                  } else {
                    loggerRef.current.info('AuthProvider: User setup completed:', setupResult);
                    // Перезагружаем профиль после setup
                    profileData = await authService.loadUserProfile(session.user.id, session);
                    loggerRef.current.info('AuthProvider: Profile reloaded after setup:', profileData);
                  }
                } catch (setupErr) {
                  loggerRef.current.error('AuthProvider: User setup error:', setupErr);
                }
              } else {
                loggerRef.current.info('AuthProvider: User has staff record, skipping setup');
                // Пытаемся загрузить профиль еще раз
                profileData = await authService.loadUserProfile(session.user.id, session);
              }
            }
            
            if (profileData) {
              const enhancedProfile: UserProfile = {
                ...profileData,
                name: profileData.fullName || session.user.email || 'Unknown User',
                email: session.user.email || '',
                avatar: undefined,
                department: undefined,
                lastLogin: undefined,
                rentalPoints: [],
              };
              loggerRef.current.debug('AuthProvider: Setting enhanced profile:', enhancedProfile);
              setProfile(enhancedProfile);
            } else {
              loggerRef.current.warn('AuthProvider: No profile data after setup attempt');
            }
            
            if (profileData && !originalRole) {
              loggerRef.current.info('AuthProvider: Setting original role', { role: profileData.role });
              setOriginalRole(profileData.role);
            }
            // Разблокируем загрузку UI сразу после получения профиля
            setIsLoading(false);
            loggerRef.current.info('AuthProvider: Profile loading completed successfully (non-critical tasks continue in background)');

            // НЕБЛОКИРУЮЩИЕ задачи: выполняем асинхронно, без await
            void (async () => {
              try {
                loggerRef.current.info('AuthProvider: Checking user data...');
                await checkUserData(session.user.id);
                loggerRef.current.info('AuthProvider: Basic user data check completed');
              } catch (e) {
                loggerRef.current.warn('AuthProvider: User data check failed (non-blocking):', e);
              }

              try {
                loggerRef.current.info('AuthProvider: Ensuring worker auth ready after profile load...');
                await ensureWorkerAuthReady(session);
                loggerRef.current.info('AuthProvider: Worker auth synchronized successfully');

                loggerRef.current.info('AuthProvider: Initializing default tags after token sync...');
                try {
                  await ensureDefaultTags(session.user.id);
                  loggerRef.current.info('AuthProvider: Default tags initialization completed');
                } catch (tagsError) {
                  loggerRef.current.warn('AuthProvider: Default tags initialization failed (non-blocking):', tagsError);
                }

                // КРИТИЧНО: Инициализация доменных статусов инвентаря
                // Это обеспечивает корректную работу на всех ОС (Windows, macOS)
                loggerRef.current.info('AuthProvider: Initializing domain statuses after token sync...');
                try {
                  await ensureDomainStatuses(session.user.id, loggerRef.current);
                  loggerRef.current.info('AuthProvider: Domain statuses initialization completed');
                } catch (domainError) {
                  loggerRef.current.warn('AuthProvider: Domain statuses initialization failed (non-blocking):', domainError);
                }
              } catch (syncError) {
                loggerRef.current.warn('AuthProvider: Worker auth sync failed (non-blocking):', syncError);
              }
            })();
          } catch (error) {
            loggerRef.current.error('AuthProvider: Profile loading error:', error);
            // Не блокируем пользователя при ошибке загрузки профиля
            loggerRef.current.info('AuthProvider: Continuing without profile data');
          } finally {
            profileLoadRef.current.inFlight = false;
          }
        } else {
          setProfile(null);
          setOriginalRole(null);
          setIsLoading(false);
        }
      }
    );

    // КРИТИЧЕСКОЕ ИСПРАВЛЕНИЕ: Убираем checkExistingSession для предотвращения зависания getSession
    // Полагаемся только на onAuthStateChange для инициализации сессии
    loggerRef.current.info('AuthProvider: Initialization completed - relying only on onAuthStateChange');

    authListenerUnsubscribe = () => subscription.unsubscribe();
    authListenerInitialized = true;

    return () => {
      authListenerMountCount -= 1;
      if (authListenerMountCount === 0 && authListenerUnsubscribe) {
        try { authListenerUnsubscribe(); } catch { /* noop */ }
        authListenerUnsubscribe = null;
        authListenerInitialized = false;
        loggerRef.current.info('AuthProvider: Auth state listener cleaned up on last unmount');
      }
    };
  }, []);

  // Отдельный эффект для таймаута завершения загрузки при отсутствии событий аутентификации
  useEffect(() => {
    const id = setTimeout(() => {
      if (isLoading && !user && !session) {
        loggerRef.current.info('AuthProvider: No auth state change detected, setting loading to false');
        setIsLoading(false);
      }
    }, 1000);

    return () => clearTimeout(id);
  }, [isLoading, user, session]);

  const wrappedHasPermission = (rolesOrPermissions: UserRole | UserRole[] | UserPermission | UserPermission[] | string | string[]): boolean => {
    return hasPermission(rolesOrPermissions);
  };

  const value: AuthContextType = {
    user,
    session,
    profile,
    isAuthenticated,
    isLoading,
    originalRole,
    signIn,
    signUp,
    signOut,
    hasPermission: wrappedHasPermission,
    switchRole,
    refreshProfile,
    signInWithPhone,
    signInWithTelegram,
    resetPassword,
    updatePassword,
    verifyOtp
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
